import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { withUnit } from '../../utils/formatUnits';

// The winder finished a spool that is shorter than a full one for this material.
// The operator confirms whether it really is an incomplete spool (-> waste with an
// estimated mass) or a cycle that was not a spool at all (threading, test run).
// Rendered by Layout for line operators; blocking like the checkpoint modal.

interface PendingCycle {
  id: number;
  ended_at: string;
  ended_at_local: string;
  length_m: number;
  nominal_length_m: number | null;
  estimated_weight_kg: number | null;
  item: { material: string | null; kolor: string | null; srednica: string | null; waga: string | null; indeks_kat: string | null } | null;
}

const POLL_MS = 30_000;

const PartialSpoolModal: React.FC = () => {
  const { user, selectedLine } = useAuth();
  const { t } = useLanguage();
  const [pending, setPending] = useState<PendingCycle[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lineId = selectedLine?.id ?? null;
  const enabled = !!user && user.role === 'user' && !!lineId;

  const fetchPending = useCallback(async () => {
    if (!enabled || !lineId) return;
    try {
      const res = await api.get('/winder-cycles/pending', { params: { line_id: lineId } });
      setPending(res.data.cycles ?? []);
    } catch {
      // keep current state
    }
  }, [enabled, lineId]);

  useEffect(() => {
    if (!enabled) return;
    fetchPending();
    const timer = setInterval(fetchPending, POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, fetchPending]);

  const current = pending[0];
  if (!enabled || !current) return null;

  const answer = async (isPartial: boolean) => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/winder-cycles/${current.id}/confirm-partial`, { is_partial: isPartial });
      setPending(prev => prev.filter(c => c.id !== current.id));
      fetchPending();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? t('partialSpool.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const item = current.item;
  const colour = item?.kolor?.includes('\n') ? item.kolor.split('\n').join('/') : item?.kolor;
  const productName = [item?.material, colour, withUnit(item?.srednica, 'mm'), withUnit(item?.waga, 'kg')].filter(Boolean).join(' ');
  const fmt = (v: number | null, d = 0) => v === null ? '—' : v.toLocaleString('pl-PL', { minimumFractionDigits: d, maximumFractionDigits: d });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.85)', zIndex: 9990,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 32, maxWidth: 520, width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)', fontFamily: 'Inter, system-ui, sans-serif', textAlign: 'center',
      }}>
        <div style={{ fontSize: 44, marginBottom: 8 }}>🧵</div>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#d97706', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
          {t('partialSpool.badge')} · {selectedLine?.name}
        </div>
        <h2 style={{ margin: '0 0 12px 0', fontSize: 22, color: '#1e293b' }}>
          {t('partialSpool.title', { length: fmt(current.length_m) })}
        </h2>

        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#1e293b' }}>{productName || item?.indeks_kat || '—'}</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            {t('partialSpool.finishedAt')}: <strong>{current.ended_at_local}</strong>
            {current.nominal_length_m !== null && <> · {t('partialSpool.fullSpool')}: <strong>{fmt(current.nominal_length_m)} m</strong></>}
            {current.estimated_weight_kg !== null && <> · {t('partialSpool.estimatedMass')}: <strong>{fmt(current.estimated_weight_kg, 2)} kg</strong></>}
          </div>
        </div>

        <p style={{ margin: '0 0 16px 0', fontSize: 17, color: '#1e293b', fontWeight: 600 }}>
          {t('partialSpool.question')}
        </p>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#991b1b' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={() => answer(true)}
            disabled={saving}
            style={{ flex: 1, padding: '16px 0', background: '#d97706', color: '#fff', border: 'none', borderRadius: 10, fontSize: 18, fontWeight: 800, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? '...' : `✔ ${t('partialSpool.yes')}`}
          </button>
          <button
            onClick={() => answer(false)}
            disabled={saving}
            style={{ flex: 1, padding: '16px 0', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 18, fontWeight: 700, cursor: 'pointer' }}
          >
            ✕ {t('partialSpool.no')}
          </button>
        </div>
        <p style={{ margin: '12px 0 0 0', fontSize: 12, color: '#94a3b8' }}>{t('partialSpool.hint')}</p>
        {pending.length > 1 && (
          <p style={{ margin: '6px 0 0 0', fontSize: 12, color: '#94a3b8' }}>{t('checkpoint.remaining', { count: pending.length })}</p>
        )}
      </div>
    </div>
  );
};

export default PartialSpoolModal;
